import type { AnalyzeSectorResponse, SectorData } from '@/types/telemetry';
import { formatTime } from '@/lib/format';
import { useI18n } from '@/context/I18nContext';

interface TheoreticalBestProps {
  result: AnalyzeSectorResponse;
  sectors: SectorData[];
}

function sectorHue(idx: number, total: number): number {
  return (idx * 360 / total) % 360;
}

export function TheoreticalBest({ result, sectors }: TheoreticalBestProps) {
  const { t } = useI18n();
  const theo = result.theoreticalBest;
  const actual = result.actualBestLap;

  if (!theo || !theo.sectors || theo.sectors.length === 0) return null;

  const totalTheo = theo.totalTimeMs;
  const totalActual = actual?.lapTimeMs || 0;
  const delta = totalActual > 0 ? totalActual - totalTheo : 0;

  // Sector bar proportions
  const sectorTotal = theo.sectors.reduce((a, s) => a + s.bestDurationMs, 0);

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center justify-center">
        <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">{t('theoBest.title')}</span>
      </div>

      <div className="text-center py-3">
        <span className="text-3xl font-bold mono" style={{ color: '#facc15', textShadow: '0 0 20px rgba(250,204,21,0.3)' }}>
          {formatTime(totalTheo)}
        </span>
      </div>

      {/* Sector bar */}
      <div className="flex gap-0.5 h-3 rounded-md overflow-hidden mb-4">
        {theo.sectors.map((s, i) => (
          <div
            key={i}
            className="rounded-sm hover:opacity-80 transition-opacity"
            style={{
              flex: s.bestDurationMs / sectorTotal,
              backgroundColor: `hsl(${sectorHue(i, theo.sectors.length)}, 70%, 55%)`,
            }}
            title={`S${i + 1}: ${formatTime(s.bestDurationMs)}`}
          />
        ))}
      </div>

      {/* Sector detail rows */}
      <div className="flex flex-col gap-1.5 mb-4">
        {theo.sectors.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: `hsl(${sectorHue(i, theo.sectors.length)}, 70%, 55%)` }}
            />
            <span className="font-semibold text-text-primary w-6">S{i + 1}</span>
            <span className="mono text-text-primary font-medium">{formatTime(s.bestDurationMs)}</span>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      {totalActual > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="divider">
                <th className="text-center py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted w-8"></th>
                <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('theoBest.type')}</th>
                <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('theoBest.time')}</th>
                <th className="text-left py-2.5 px-4 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('theoBest.delta')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="divider hover:bg-bg-hover transition-colors">
                <td className="text-center py-2.5 px-4 text-base">🥇</td>
                <td className="py-2.5 px-4 text-xs text-text-muted">{t('theoBest.theoretical')}</td>
                <td className="py-2.5 px-4 mono font-semibold text-yellow">
                  {formatTime(totalTheo)}
                </td>
                <td className="py-2.5 px-4 mono text-text-muted">—</td>
              </tr>
              <tr className="hover:bg-bg-hover transition-colors">
                <td className="text-center py-2.5 px-4 text-base">🏁</td>
                <td className="py-2.5 px-4 text-xs text-text-muted">{t('theoBest.actualBest')}</td>
                <td className="py-2.5 px-4 mono font-semibold text-text-secondary">
                  {formatTime(totalActual)}
                </td>
                <td className="py-2.5 px-4 mono text-red font-medium">
                  +{formatTime(delta)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
